import { type PermissionKey } from '@aysar/contracts';
import { type TFunction } from 'i18next';

import { type FormSectionDef } from '@/shared/components/dynamic-form/types/form-section.types';

import { PermissionChecklist } from '../components/PermissionChecklist/PermissionChecklist';

/** Narrows the unknown a custom field carries back to the permission list. */
function toPermissionKeys(value: unknown): PermissionKey[] {
  return Array.isArray(value) ? (value as PermissionKey[]) : [];
}

/**
 * The role editor's sections.
 *
 * In `config/` rather than in the component, because a field list is a
 * declaration: it is read by the form, and it will be read by whatever P10 adds
 * beside it. `*.config.*` is also one of U22's identity roles, so it sits at the
 * folder root by design rather than by exception.
 *
 * Two groups, because they are two different questions: **what this role is**
 * (its key and its translated name), and **what it may do**. The key now sits
 * beside the Arabic name it is derived from instead of a screen away from it —
 * translatable fields render in place since P10.2d.
 *
 * `permissions` is a `custom` field whose `render` is CALLED by `FieldRenderer`
 * and returns a real, module-scope component — never a component identity built
 * during render, which React Compiler's `static-components` rule rejects and
 * which would remount the whole checklist on every keystroke elsewhere in the
 * form. It declares `isSelfNaming`, because `PermissionChecklist` composes its
 * own group name and a `<Label htmlFor>` beside it would point at nothing.
 */
export function buildRoleFormSections(t: TFunction, isEditing: boolean): FormSectionDef[] {
  return [
    {
      id: 'role-identity',
      title: t('roles.form.sections.identity'),
      fields: [
        {
          name: 'key',
          kind: 'slug',
          label: t('roles.form.key'),
          description: t('roles.form.keyHint'),
          // Derived from the name, in the api's `lower_snake_case` grammar. An
          // Arabic name derives to nothing and `SlugField` says so.
          sourceName: 'name',
          separator: '_',
          maxLength: 64,
          isRequired: true,
          isLockedAfterCreate: true,
          // Immutable server-side: seeds, guards and audit rows reference roles
          // by key, so renaming one would silently re-point every one of them.
          isDisabled: isEditing,
        },
        {
          name: 'name',
          kind: 'text',
          label: t('roles.form.name'),
          isTranslatable: true,
          isRequired: true,
          maxLength: 120,
        },
        {
          name: 'description',
          kind: 'textarea',
          label: t('roles.form.description'),
          isTranslatable: true,
          colSpan: 'full',
          maxLength: 500,
        },
      ],
      // Two tracks at most: a key and a name are short, and a third column of
      // them on a wide screen reads as three unrelated boxes.
      columns: 2,
    },
    {
      id: 'role-permissions',
      title: t('roles.form.permissions'),
      description: t('roles.form.permissionsHint'),
      // The checklist is its own multi-column layout inside; a grid around it
      // would be a second one arguing with the first.
      columns: 1,
      fields: [
        {
          name: 'permissions',
          kind: 'custom',
          label: t('roles.form.permissions'),
          isSelfNaming: true,
          render: (control) => (
            <PermissionChecklist
              value={toPermissionKeys(control.value)}
              isDisabled={control.isDisabled}
              describedById={control.describedById}
              onChange={control.onChange}
            />
          ),
        },
      ],
    },
  ];
}
